/**
 * Az ügyfél PWA váza — MOBIL-ELSŐDLEGES.
 *
 * AZ ÜGYFÉLNEK EGY MUNKAKÉPERNYŐJE VAN: „mit kérnek tőlem", a hiánylista
 * tükre. Ez kemény szabály, nem egyszerűsítés.
 *
 * ÉS AMIT AZ ÜGYFÉL SOHA NEM LÁT: a „bizonyított" és a „gyanított" szót
 * (2.5b). Ezért nem a `common` állapot-feliratait használja ez a felület,
 * hanem a saját `client` névterét — a szótár szétválasztása fizikai: az
 * `admin.json` ide EL SEM MÁSOLÓDIK (`scripts/shared-sync.py`).
 *
 * A JELSZÓÁLLÍTÁS NEM NEGYEDIK FELÜLET. A `CLAUDE.md` szabálya a MUNKA-
 * képernyőkről szól; a belépés annak az előszobája, nem új tengely. Ugyanígy
 * nem az a belépő űrlap sem.
 *
 * A FELTÖLTÉS a §8c 7–8. lépésével megérkezett: a munkaképernyő a
 * `features/requests/RequestScreen.tsx`-ben él. Ez a fájl HÁROM dolgot dönt
 * el, és semmi mást: kell-e jelszót állítani a levél linkjéről, kell-e
 * belépés, vagy jöhet a munkaképernyő.
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PasswordInput } from '@/components/common';
import { useAuth } from '@/features/auth/AuthProvider';
import { ImpersonationBanner } from '@/features/impersonation/ImpersonationBanner';
import { useImpersonationHandoff } from '@/features/impersonation/useImpersonationHandoff';
import { RequestScreen } from '@/features/requests/RequestScreen';
import { ApiError } from '@/lib/api-client';

/**
 * A VARÁZSLINK TOKENJE A HORGONYBÓL JÖN (`…#belepes=<token>`), nem a
 * lekérdezésből — a backend `magicLinkUrl()`-je írja így.
 *
 * MIÉRT A HORGONY. A böngésző a `#` utáni részt NEM küldi el a szervernek.
 * Egy belépést érő titok így nem kerül bele sem a statikus kiszolgáló
 * naplójába, sem a `Referer` fejlécbe egy továbbkattintásnál.
 *
 * EZ A FÜGGVÉNY TISZTA — nem nyúl a címsorhoz. A React szigorú módja fejlesztés
 * közben KÉTSZER hívja meg az állapot-kezdőértékeket; ha a törlés itt lenne,
 * a második hívás már üres horgonyt találna, és a token eltűnne a felület
 * alól. A törlés ezért mellékhatásként, a beolvasás UTÁN történik.
 */
function readMagicLinkToken(): string | null {
  const match = /(?:^|&)belepes=([^&]+)/.exec(window.location.hash.replace(/^#/, ''));
  if (match === null) {
    return null;
  }

  try {
    return decodeURIComponent(match[1]);
  } catch {
    // A `decodeURIComponent` HIBÁS SZÁZALÉKJELRE DOB (`#belepes=%`), és ez a
    // függvény RENDERELÉS közben fut — egy kivétel itt nem egy rossz linket
    // jelentene, hanem fehér képernyőt az EGÉSZ alkalmazás helyén. A saját
    // tokenjeink base64url-ök, tehát sosem ilyenek; a címsor viszont bárkitől
    // jöhet, és a levelezőprogramok át szokták írni a linkeket.
    return null;
  }
}

/** A szerver magyar mondata, ha jött — hálózati hibánál `null`, és a hívó ad sajátot. */
function serverMessage(caught: unknown): string | null {
  return caught instanceof ApiError && caught.message ? caught.message : null;
}

export default function App() {
  const { me, loading, sessionEnd } = useAuth();
  // A MASZK ÁTADÓ KÓDJA (#76) — ha a cím hozta, a beváltás ELŐBB fut, mint
  // bármilyen döntés arról, ki van itt: egy ezen a gépen ülő ügyfél-munkamenet
  // helyére a könyvelő maszkja kerül, nem mellé.
  const handoff = useImpersonationHandoff();
  const [magicToken, setMagicToken] = useState<string | null>(readMagicLinkToken);

  // A CÍMSORT RÖGTÖN KITAKARÍTJUK. A token a memóriában marad, amíg a
  // képernyő él — a képernyőn viszont, a megosztható címben, nincs
  // keresnivalója. Egy megmutatott vagy elmentett cím különben belépést érne.
  useEffect(() => {
    if (magicToken !== null && window.location.hash !== '') {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  }, [magicToken]);

  // AMINT VAN MUNKAMENET, A TOKEN TÁRGYTALAN — és nem csak fölösleges.
  // Nélküle a KILÉPÉS után (`me` újra `null`) a jelszóállító képernyő jönne
  // vissza, ahol a link a harmadik kapun elbukik (már van jelszó), és a
  // frissen bekötött ügyfél első látványa a „Ez a link lejárt. Kérj újat a
  // könyvelődtől." mondat lenne. Épp az a telefonhívás, amit a 2b döntése
  // meg akar szüntetni.
  useEffect(() => {
    if (me !== null) {
      setMagicToken(null);
    }
  }, [me]);

  const forgetToken = useCallback(() => {
    setMagicToken(null);
  }, []);

  if (loading || handoff.kind === 'pending') {
    return null;
  }

  if (handoff.kind === 'failed') {
    return <ImpersonationClosed reason="linkInvalid" message={handoff.message} />;
  }

  // A SORREND SZÁMÍT: aki már be van lépve, annak a link tárgytalan — a
  // jelszava megvan, a `redeem` úgyis elutasítaná.
  //
  // ⚠️ ÖSSZEFÉSÜLÉS (09-02): a varázslink-ág `<Requests />`-t adott vissza, a
  // feltöltés-ág `<RequestScreen />`-t — UGYANAZ a munkaképernyő, két néven.
  // A `RequestScreen` a végleges, mert az hordozza a feltöltést is.
  if (me) {
    return (
      <>
        <ImpersonationBanner />
        <RequestScreen />
      </>
    );
  }
  // A LEZÁRULT MASZK UTÁN NEM A BELÉPŐ KÉPERNYŐ JÖN (#76): a könyvelő nem a
  // saját nevén akar az ügyfél felületére belépni — ezt a lapot zárja be.
  if (sessionEnd !== null) {
    return <ImpersonationClosed reason={sessionEnd} message={null} />;
  }
  return magicToken === null ? (
    <SignIn />
  ) : (
    <SetPassword token={magicToken} onGiveUp={forgetToken} />
  );
}

function SignIn() {
  const { t } = useTranslation();
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(email, password);
    } catch (caught) {
      setError(serverMessage(caught) ?? t('error.unexpected'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="centered">
      <Brand />
      <form className="card" onSubmit={onSubmit}>
        <h1>{t('auth.signInTitle')}</h1>

        <label htmlFor="email">{t('auth.email')}</label>
        <input
          id="email"
          type="email"
          inputMode="email"
          autoComplete="username"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />

        <label htmlFor="password">{t('auth.signInPassword')}</label>
        <PasswordInput
          id="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />

        {error ? (
          <p className="error" role="alert">
            {error}
          </p>
        ) : null}

        <button type="submit" disabled={busy}>
          {t('auth.signIn')}
        </button>
      </form>
    </main>
  );
}

/**
 * A VARÁZSLINK KÉPERNYŐJE — az ügyfél ELSŐ belépése (§2b, §8c 6. lépés).
 *
 * Nem „bejelentkezés": az ügyfél itt JELSZÓT ÁLLÍT, és utána már rendes
 * belépéssel jön. A varázslink a regisztrációt váltja ki, nem a jelszót.
 *
 * A LINK ÉRVÉNYESSÉGÉT ELŐRE MEGKÉRDEZZÜK. Enélkül az ügyfél kétszer begépel
 * egy jelszót, és csak a küldés után derül ki, hogy a link három hete lejárt.
 * A `checkMagicLink` ezért nem hibát dob érvénytelen linkre — az itt hétköznapi
 * ág, nem kivétel.
 */
function SetPassword({ token, onGiveUp }: { token: string; onGiveUp: () => void }) {
  const { t } = useTranslation();
  const { checkMagicLink, redeemMagicLink } = useAuth();
  const [name, setName] = useState<string | null>(null);
  const [valid, setValid] = useState<boolean | null>(null);
  const [password, setPassword] = useState('');
  const [passwordAgain, setPasswordAgain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** A jelszó elmentődött, de a munkamenet nem állt fel — lásd `redeemMagicLink`. */
  const [passwordSet, setPasswordSet] = useState(false);

  useEffect(() => {
    let alive = true;

    checkMagicLink(token)
      .then((result) => {
        if (!alive) {
          return;
        }
        setValid(result.valid);
        setName(result.name);
      })
      .catch(() => {
        // Hálózati hiba is „nem tudjuk megnyitni" — a felület nem tesz
        // különbséget, mert az ügyfélnek a teendő ugyanaz.
        if (alive) {
          setValid(false);
        }
      });

    return () => {
      alive = false;
    };
  }, [checkMagicLink, token]);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();

    // A KÉT JELSZÓ EGYEZÉSE HELYI KÉRDÉS. A szervernek egy jelszót küldünk;
    // az elgépelés ellen a második mező véd, és arról a felület dönt.
    if (password !== passwordAgain) {
      setError(t('auth.mismatch'));
      return;
    }

    setBusy(true);
    setError(null);
    try {
      // `signedIn`-nél innen nincs teendő: az `App` a `me`-re vált.
      if ((await redeemMagicLink(token, password)) === 'passwordSet') {
        // ⚠️ AZ ŰRLAPON HAGYVA az ügyfél újra beküldené, és a már elhasznált
        // tokenre „lejárt link" jönne — holott a jelszava rendben van.
        setPasswordSet(true);
      }
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        // A link a két képernyő között járt le, vagy valaki más váltotta be.
        setValid(false);
        return;
      }
      setError(serverMessage(caught) ?? t('error.unexpected'));
    } finally {
      setBusy(false);
    }
  }

  if (valid === null) {
    // A LINK ELLENŐRZÉSE ALATT NEM MUTATUNK ŰRLAPOT. Egy fél másodpercre
    // felvillanó jelszómező, ami aztán „lejárt"-ra vált, rosszabb, mint a
    // csend.
    return null;
  }

  if (passwordSet) {
    return (
      <main className="centered">
        <Brand />
        <div className="card">
          <h1>{t('auth.linkTitle')}</h1>
          <output className="notice">{t('auth.passwordSaved')}</output>
          <button type="button" onClick={onGiveUp}>
            {t('auth.signIn')}
          </button>
        </div>
      </main>
    );
  }

  if (!valid) {
    return (
      <main className="centered">
        <Brand />
        <div className="card">
          <h1>{t('auth.linkTitle')}</h1>
          <p className="error" role="alert">
            {t('auth.linkExpired')}
          </p>
          <button type="button" onClick={onGiveUp}>
            {t('auth.signIn')}
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="centered">
      <Brand />
      <form className="card" onSubmit={onSubmit}>
        <h1>{t('auth.linkTitle')}</h1>
        {name === null ? null : <p className="intro">{t('auth.linkGreeting', { name })}</p>}
        <p className="intro">{t('auth.linkIntro')}</p>

        <label htmlFor="new-password">{t('auth.password')}</label>
        <PasswordInput
          id="new-password"
          autoComplete="new-password"
          required
          // A HOSSZSZABÁLY ELŐRE LÁTSZIK, nem a küldés után. A szerver
          // ugyanezt kényszeríti (`MagicLinkRedeemDto`); enélkül az ügyfél
          // kétszer begépel egy jelszót, és egy 400-as válaszból tudja meg,
          // hogy rövid volt.
          minLength={10}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        <p className="intro">{t('auth.passwordHint', { min: 10 })}</p>

        <label htmlFor="new-password-again">{t('auth.passwordAgain')}</label>
        <PasswordInput
          id="new-password-again"
          autoComplete="new-password"
          required
          minLength={10}
          value={passwordAgain}
          onChange={(event) => setPasswordAgain(event.target.value)}
        />

        {error ? (
          <p className="error" role="alert">
            {error}
          </p>
        ) : null}

        <button type="submit" disabled={busy}>
          {t('auth.submit')}
        </button>
      </form>
    </main>
  );
}

const CLOSED_MESSAGES = {
  ended: 'impersonation.ended',
  switched: 'impersonation.switched',
  linkInvalid: 'impersonation.linkInvalid',
} as const;

/**
 * A MUNKAMENET VÉGE EZEN A LAPON (#76) — a maszk lezárult (kilépés, lejárat,
 * visszavonás), az átadó link már nem érvényes, vagy ebben a böngészőben
 * időközben más lépett be. Belépő űrlap szándékosan nincs itt: a teendő a
 * lap bezárása, illetve az újratöltés.
 */
function ImpersonationClosed({
  reason,
  message,
}: {
  reason: 'ended' | 'switched' | 'linkInvalid';
  message: string | null;
}) {
  const { t } = useTranslation();

  return (
    <main className="centered">
      <Brand />
      <div className="card">
        <h1>{t('impersonation.closedTitle')}</h1>
        <p>
          <output>{message ?? t(CLOSED_MESSAGES[reason])}</output>
        </p>
        <p className="meta">{t('impersonation.closeTab')}</p>
      </div>
    </main>
  );
}

/**
 * AZ ELŐSZOBA FEJE (#57) — a belépés és a jelszóállítás fölött ugyanaz.
 *
 * A munkaképernyő fejlécének párja: aki a levél linkjéről érkezik,
 * már itt lássa, hogy jó helyen jár, ne csak egy névtelen űrlapot.
 *
 * A JEL A FAVICON, nem egy betű *(Viktor, 2026-10-19)*: ugyanaz a lap a
 * felfelé mutató nyíllal, amit az ügyfél a böngészőfülön és a kezdőképernyőn
 * lát. Az `alt` üres, mert a név mellette áll — a képernyőolvasónak kétszer
 * mondaná ugyanazt.
 */
function Brand() {
  const { t } = useTranslation();

  return (
    <div className="brand">
      <img className="brand-mark" src="/favicon.svg" alt="" width="40" height="40" />
      <span className="brand-name">{t('app.name')}</span>
    </div>
  );
}
